import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import LocalNavbar from "../LoginCredentialsTemporaryPassword/LocalComponents/LocalNavbar";
import loadingGif from "../../../Assets/Images/Animations/loading-management-console.gif";
import expiredIllustration from "../../../Assets/Images/Illustrations/failure.svg";
import invalidIllustration from "../../../Assets/Images/Illustrations/delete-card-img.svg";
import styles from "./loginRedirectionForgetPassword.module.scss";
import { userApi } from "../../../Utils/API";
import { VALIDATE_FORGET_PASSWORD_TOKEN } from "../../../Utils/GlobalConstants";

// API: GET /user/api/user/validate-token?token={token}
// Response shape:
//   { tokenIsValid: true,  redirectUri: "https://...", message: "Token is valid." }
//   { tokenIsValid: false, redirectUri: "...",          message: "..." }
// 400 → invalid token  |  other errors → treat as expired (safe default)

const STEPS = [
  { key: "validating", label: "Validating Link" },
  { key: "redirecting", label: "Redirecting" },
];

const LoginRedirectionForgetPassword = () => {
  // Raw token extraction — preserves + characters in Base64 tokens.
  // URLSearchParams.get("token") decodes + → space and corrupts such tokens.
  const rawQuery = window.location.search;
  const tokenMatch = rawQuery.match(/[?&]token=([^&]*)/);
  const token = tokenMatch ? tokenMatch[1] : null;

  // "loading" | "redirecting" | "expired" | "invalid"
  const [pageState, setPageState] = useState("loading");

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!token) {
      setPageState("expired");
      return;
    }

    userApi
      .get(`${VALIDATE_FORGET_PASSWORD_TOKEN}?token=${token}`)
      .then((resp) => {
        const data = resp.data;
        if (data && data.tokenIsValid === true && data.redirectUri) {
          // Hold "Validating Link" state for 1s so user can see it,
          // then switch to "Redirecting" and wait 3s before navigating.
          setTimeout(() => {
            setPageState("redirecting");
            setTimeout(() => {
              window.location.href = data.redirectUri;
            }, 3000);
          }, 1000);
        } else {
          setPageState("expired");
        }
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 400) {
          setPageState("invalid");
        } else {
          setPageState("expired");
        }
      });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Loading / Redirecting — branded status card ───────────────────────────
  const renderStatusCard = () => {
    const activeStep = pageState === "redirecting" ? 1 : 0;
    const title = pageState === "redirecting" ? "Redirecting You" : "Validating Your Link";
    const subtitle =
      pageState === "redirecting"
        ? "Your link is verified. Taking you to the password reset page…"
        : "Please wait while we verify your password reset link.";

    return (
      <div className={styles["status-card"]}>
        {/* Top brand bar */}
        <div className={styles["status-card-bar"]} />

        {/* Spinner */}
        <div className={styles["spinner-wrap"]}>
          <img
            src={loadingGif}
            alt={title}
            className={styles["spinner-gif"]}
          />
        </div>

        {/* Title + Subtitle */}
        <h2 className={styles["status-title"]}>{title}</h2>
        <p className={styles["status-subtitle"]}>{subtitle}</p>

        {/* Step indicators */}
        <div className={styles["steps"]}>
          {STEPS.map((step, idx) => (
            <div
              key={step.key}
              className={`${styles["step"]} ${
                idx < activeStep
                  ? styles["step--done"]
                  : idx === activeStep
                  ? styles["step--active"]
                  : styles["step--pending"]
              }`}
            >
              <div className={styles["step-dot"]}>
                {idx < activeStep && (
                  <svg viewBox="0 0 12 12" className={styles["step-check"]}>
                    <polyline points="2,6 5,9 10,3" />
                  </svg>
                )}
              </div>
              <span className={styles["step-label"]}>{step.label}</span>
            </div>
          ))}
        </div>

        {/* Progress bar — shown only during redirecting */}
        {pageState === "redirecting" && (
          <div className={styles["progress-bar-track"]}>
            <div className={styles["progress-bar-fill"]} />
          </div>
        )}
      </div>
    );
  };

  // ── Expired State ─────────────────────────────────────────────────────────
  const renderExpired = () => (
    <div className={styles["state-card"]}>
      <img
        src={expiredIllustration}
        alt="Link expired"
        className={styles["state-illustration"]}
      />
      <h2 className={styles["state-title"]}>Link Expired</h2>
      <p className={styles["state-body"]}>
        This password reset link is no longer valid. Links expire after a set
        period or may have already been used.
      </p>
      <p className={styles["state-body"]}>
        Please request a new password reset link, or contact your Synnex account
        manager at{" "}
        <a
          href="mailto:online-support@au.synnex-grp.com"
          className={styles["state-link"]}
        >
          online-support@au.synnex-grp.com
        </a>
        .
      </p>
    </div>
  );

  // ── Invalid Token State ───────────────────────────────────────────────────
  const renderInvalid = () => (
    <div className={styles["state-card"]}>
      <img
        src={invalidIllustration}
        alt="Invalid link"
        className={styles["state-illustration"]}
      />
      <h2 className={styles["state-title"]}>Invalid Link</h2>
      <p className={styles["state-body"]}>
        The password reset link you followed is not valid. This may happen if
        the link was copied incorrectly, is incomplete, or has been tampered
        with.
      </p>
      <p className={styles["state-body"]}>
        Please use the exact link sent to your registered email address. If you
        continue to experience issues, contact your Synnex account manager at{" "}
        <a
          href="mailto:online-support@au.synnex-grp.com"
          className={styles["state-link"]}
        >
          online-support@au.synnex-grp.com
        </a>
        .
      </p>
    </div>
  );

  // ── Main Render ───────────────────────────────────────────────────────────
  // page-content--centered applied to ALL states so the card never jumps
  // position between validation → redirect → error transitions.
  return (
    <div className={styles["page-wrapper"]}>
      <LocalNavbar />

      <div className={`${styles["page-content"]} ${styles["page-content--centered"]}`}>
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} sm={10} md={8} lg={6} xl={5}>
              {(pageState === "loading" || pageState === "redirecting") && renderStatusCard()}
              {pageState === "expired" && renderExpired()}
              {pageState === "invalid" && renderInvalid()}
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default LoginRedirectionForgetPassword;
